import { useEffect, useMemo, useRef, useState } from 'react'
import './featured-content.styles.scss'
import placeholder_icon from '@/assets/mystery-challenge.png'
import ImageContainer from '@/components/SHARED/image-container/image-container.component';
import Modal, { ModalAlert } from '@/components/modal/modal.component';
import SearchBar from '@/components/SHARED/search-bar/search-bar.component';
import CategoryFilterModal from '../create-elements/components/category-filter-modal.component';
import { useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faCaretUp, faFilter } from '@fortawesome/free-solid-svg-icons';
import { UseCloudFunction } from '@/utils/firebase.utils';
import { useSelector } from 'react-redux';
import ThreeDotsLoader from '@/components/loader/three-dots-loader.component';
import useAnalytics from '@/utils/useAnalytics';
import {
    FEATURED_MAX,
    getFeaturedIds,
    getFeaturedTypeLabel,
    isFeaturableElement
} from '@/utils/featuredElements';

// delay before a checked element moves into (or out of) the selected list
const MOVE_DELAY = 500;

const FeaturedContent = ({
    gameData,
    gameElements,
    role
}) => {

    const userProfile = useSelector(state => state.userData.userProfile);
    const membership = useSelector(state => state.userData.membership);
    const [ showChoiceModal, setShowChoiceModal ] = useState(false);
    const [ showFilterModal, setShowFilterModal ] = useState(false);
    const [ selectedTypes, setSelectedTypes ] = useState([]);
    const [ searchText, setSearchText ] = useState('');
    const [ selectedIds, setSelectedIds ] = useState([]);
    const [ checkedIds, setCheckedIds ] = useState([]);
    const [ alertMessage, setAlertMessage ] = useState(null);
    const [ loading, setLoading ] = useState(false);
    const { trackEvent, trackException } = useAnalytics();
    const timersRef = useRef({});
    const location = useLocation();
    const canEdit = role === 'owner' || role === 'manager';

    const featurableElements = useMemo(() => (
        gameElements
        ? Object.values(gameElements)
            .filter(isFeaturableElement)
            .sort((a,b) => a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1)
        : []
    ), [gameElements])

    const savedElements = useMemo(() => (
        getFeaturedIds(gameData)
        .map(id => gameElements?.[id])
        .filter(isFeaturableElement)
    ), [gameData, gameElements])

    const typeList = useMemo(() => (
        [...new Set(featurableElements.map(getFeaturedTypeLabel))].sort()
    ), [featurableElements])

    const unselectedToDisplay = featurableElements
        .filter(e =>
            !selectedIds.includes(e.id) &&
            (selectedTypes.length === 0 || selectedTypes.includes(getFeaturedTypeLabel(e))) &&
            (
                !searchText ||
                e.name.toLowerCase().includes(searchText) ||
                (e.desc && e.desc.toLowerCase().includes(searchText))
            )
        );

    useEffect(() => {
        return () => clearTimers();
    }, [])

    function clearTimers() {
        Object.values(timersRef.current).forEach(t => clearTimeout(t));
        timersRef.current = {};
    }

    function openModal() {
        if (!canEdit) return;
        const ids = savedElements.map(e => e.id);
        clearTimers();
        setSelectedIds(ids);
        setCheckedIds(ids);
        setSearchText('');
        setSelectedTypes([]);
        setShowChoiceModal(true);
    }

    function closeModal() {
        clearTimers();
        setShowChoiceModal(false);
    }

    function toggleElement(id) {
        const isChecked = checkedIds.includes(id);
        if (!isChecked && checkedIds.length >= FEATURED_MAX) {
            setAlertMessage(`You can feature up to ${FEATURED_MAX} elements.`);
            return;
        }
        setCheckedIds(prev => isChecked ? prev.filter(c => c !== id) : [...prev, id]);
        clearTimeout(timersRef.current[id]);
        timersRef.current[id] = setTimeout(() => {
            delete timersRef.current[id];
            setSelectedIds(prev =>
                isChecked
                ? prev.filter(s => s !== id)
                : prev.includes(id) ? prev : [...prev, id]
            );
        }, MOVE_DELAY);
    }

    function moveElement(index, direction) {
        const newIndex = index + direction;
        if (newIndex < 0 || newIndex >= selectedIds.length) return;
        const arr = [...selectedIds];
        [arr[index], arr[newIndex]] = [arr[newIndex], arr[index]];
        setSelectedIds(arr);
    }

    function chooseType(type) {
        setSelectedTypes(prev =>
            prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
        );
    }

    function search(text) {
        setSearchText(text ? text.toLowerCase() : '');
    }

    async function saveFeaturedElements() {
        if (location.pathname.includes('/preview/manager/')) {
            setAlertMessage('Sorry! Data cannot be saved in Preview Mode.');
            return;
        }
        // include any checks still waiting on the move delay
        const featuredElements = [
            ...selectedIds.filter(id => checkedIds.includes(id)),
            ...checkedIds.filter(id => !selectedIds.includes(id))
        ];
        setLoading(true);
        let tempData = {
            ...gameData,
            ...{
                'featuredElements': featuredElements,
                'featuredElement': null,
                'ts': new Date().getTime(),
            }
        }
        const res = await UseCloudFunction(
            'saveGame',
            {
                'gameId': tempData.gameId,
                'gameObj': tempData,
                'profile': userProfile,
                'action': 'set featured elements'
            })
        setLoading(false);
        if (res.error) {
            setAlertMessage(`Something went wrong. (Error: ${res.error})`);
            trackException(`Featured Elements Update Error: ${res.error}`);
            return;
        }
        trackEvent(
            'featured_element_updated',
            'Game Manager',
            `Featured elements updated for game ${gameData.gameId}.`,
            null,
            membership
        );
        closeModal();
    }

    function renderChoice(element, index) {
        const isSelected = index !== undefined;
        const isChecked = checkedIds.includes(element.id);
        return (
            <div
                key={element.id}
                className={`element-item g-list-item ${isChecked ? 'chosen' : ''}`}
            >
                <label className='element-info clickable'>
                    <input
                        type='checkbox'
                        checked={isChecked}
                        onChange={() => toggleElement(element.id)}
                    />
                    <ImageContainer src={showChoiceModal ? element.icon : ''} alt={`${element.name} icon`} className='element-icon' />
                    <div>
                        {element.name}
                        <div className='meta'>{getFeaturedTypeLabel(element)}</div>
                    </div>
                </label>
                {
                    (isSelected) &&
                    <div className='order-buttons'>
                        <button
                            type='button'
                            className={`g-button small-btn ${index === 0 ? 'disabled' : ''}`}
                            title='Move up'
                            onClick={() => moveElement(index, -1)}
                        >
                            <FontAwesomeIcon icon={faCaretUp} />
                        </button>
                        <button
                            type='button'
                            className={`g-button small-btn ${index === selectedIds.length - 1 ? 'disabled' : ''}`}
                            title='Move down'
                            onClick={() => moveElement(index, 1)}
                        >
                            <FontAwesomeIcon icon={faCaretDown} />
                        </button>
                    </div>
                }
            </div>
        )
    }

    return (
        <div className='featured-content-container'>
            <div className='featured-element'>
                {
                    (savedElements.length > 0)
                    ?
                    <div className='featured-element-list'>
                        {
                            savedElements.map((element, index) => (
                                <div key={element.id} className='featured-element-row'>
                                    <span className='order-number'>{index + 1}</span>
                                    <ImageContainer src={element.icon} alt={`${element.name} icon`} className='featured-row-icon' />
                                    <div className='featured-row-info'>
                                        <div className='name'>{element.name}</div>
                                        <div className='meta'>{getFeaturedTypeLabel(element)}</div>
                                    </div>
                                </div>
                            ))
                        }
                    </div>
                    :
                    <div className='featured-element-content'>
                        <img src={placeholder_icon} alt='mystery placeholder icon' className='featured-icon' />
                        <div>No featured elements chosen.<br /><span className='meta'>Players will see random elements available to them.</span></div>
                    </div>
                }
                <div className='center'>
                    <button
                        className={`g-button small-btn ${!canEdit ? 'disabled' : ''}`}
                        onClick={() => openModal()}>
                        Edit Featured
                    </button>
                </div>
            </div>
            <Modal show={showChoiceModal} cancel={() => closeModal()} text='Choose featured game elements' closeButton={true} >
                <div className='featured-modal-content'>
                    <div className='meta'>
                        Choose up to {FEATURED_MAX} elements and arrange them in the order players will see them. If fewer than {FEATURED_MAX} are chosen, the rest will be filled with random elements each player has access to.
                    </div>
                    <div className='featured-search'>
                        <SearchBar search={search} />
                        <button type='button' className='g-button med-btn' onClick={() => setShowFilterModal(true)}>
                            Filter by Type
                            <FontAwesomeIcon
                                icon={faFilter}
                                className={selectedTypes.length > 0 ? 'full' : 'empty'}
                            />
                        </button>
                    </div>

                    <div className='elements-to-choose'>
                        <div className='list-heading'>
                            Featured ({checkedIds.length}/{FEATURED_MAX})
                        </div>
                        {
                            (selectedIds.length > 0)
                            ?
                            selectedIds
                            .map((id, index) => gameElements?.[id] && renderChoice(gameElements[id], index))
                            :
                            <div className='meta'>Nothing chosen yet. Check an element below to feature it.</div>
                        }
                        <hr />
                        {
                            (unselectedToDisplay.length > 0)
                            ?
                            unselectedToDisplay.map(e => renderChoice(e))
                            :
                            <div className='meta'>No elements found.</div>
                        }
                    </div>
                    {
                        (loading)
                        ?
                        <div className='buttons'>
                            <button className='g-button' >Cancel</button>
                            <button className='g-button' ><ThreeDotsLoader /></button>
                        </div>
                        :
                        <div className='buttons'>
                            <button className='g-button' onClick={() => closeModal()}>Cancel</button>
                            <button className={`g-button primary`} onClick={() => saveFeaturedElements()}>Save Featured Elements</button>
                        </div>
                    }
                </div>
            </Modal>
            <CategoryFilterModal
                show={showFilterModal}
                cancel={() => setShowFilterModal(false)}
                chooseCategory={chooseType}
                categoryList={typeList}
                selectedCategories={selectedTypes}
                text='Filter by Type'
                color='var(--primary)'
            />
            <ModalAlert show={alertMessage} cancel={() => setAlertMessage(null)} message={alertMessage} />
        </div>
    )
}

export default FeaturedContent
